import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { MapPin } from "../types";
import {
  hasGoogleMapsApiKey,
  pinEligibleForPlacesPhoto,
  resolvePlacePhoto,
  type PlacePhotoResult,
} from "../lib/googlePlaces";
import {
  getCachedPlacePhoto,
  invalidateCachedPlacePhoto,
  setCachedPlacePhoto,
} from "../lib/placePhotoCache";

type PhotoEntry = {
  status: "idle" | "loading" | "ready" | "error";
  result?: PlacePhotoResult;
};

type PlacePhotosContextValue = {
  getPhoto: (pinId: string) => PlacePhotoResult | undefined;
  getStatus: (pinId: string) => PhotoEntry["status"];
  /** Drop Places photo (e.g. media URL expired) and fall back to Commons. */
  markBroken: (pinId: string) => void;
  enabled: boolean;
};

const PlacePhotosContext = createContext<PlacePhotosContextValue | null>(null);

const CONCURRENCY = 3;

async function runPool<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
  signal: AbortSignal,
) {
  let i = 0;
  const runners = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (i < items.length) {
        if (signal.aborted) return;
        const idx = i++;
        await worker(items[idx]);
      }
    },
  );
  await Promise.all(runners);
}

export function PlacePhotosProvider({
  pins,
  children,
  onPlaceIdResolved,
}: {
  pins: MapPin[];
  children: ReactNode;
  /** Persist Google placeId onto trip pins when Places enrichment resolves one. */
  onPlaceIdResolved?: (pinId: string, placeId: string) => void;
}) {
  const enabled = hasGoogleMapsApiKey();
  const [byId, setById] = useState<Record<string, PhotoEntry>>({});
  const inFlight = useRef(new Set<string>());
  const broken = useRef(new Set<string>());
  const onPlaceIdResolvedRef = useRef(onPlaceIdResolved);
  onPlaceIdResolvedRef.current = onPlaceIdResolved;

  const eligible = useMemo(
    () => (enabled ? pins.filter((p) => pinEligibleForPlacesPhoto(p)) : []),
    [pins, enabled],
  );

  useEffect(() => {
    if (!enabled || eligible.length === 0) return;

    const ac = new AbortController();
    const initial: Record<string, PhotoEntry> = {};

    for (const pin of eligible) {
      if (broken.current.has(pin.id)) {
        initial[pin.id] = { status: "error" };
        continue;
      }
      const cached = getCachedPlacePhoto(pin.id);
      if (cached) {
        initial[pin.id] = { status: "ready", result: cached };
        const resolvedId = cached.placeId?.trim();
        if (resolvedId && !pin.placeId?.trim()) {
          onPlaceIdResolvedRef.current?.(pin.id, resolvedId);
        }
      }
    }
    if (Object.keys(initial).length > 0) {
      setById((prev) => ({ ...prev, ...initial }));
    }

    const needFetch = eligible.filter(
      (p) =>
        !broken.current.has(p.id) &&
        !initial[p.id] &&
        !inFlight.current.has(p.id),
    );

    void runPool(
      needFetch,
      CONCURRENCY,
      async (pin) => {
        if (ac.signal.aborted) return;
        inFlight.current.add(pin.id);
        setById((prev) => ({
          ...prev,
          [pin.id]:
            prev[pin.id]?.status === "ready"
              ? prev[pin.id]
              : { status: "loading" },
        }));
        try {
          const result = await resolvePlacePhoto(
            {
              id: pin.id,
              name: pin.name,
              lat: pin.lat,
              lng: pin.lng,
              mapsQuery: pin.mapsQuery,
              placeId: pin.placeId,
            },
            ac.signal,
          );
          if (ac.signal.aborted) return;
          if (result) {
            setCachedPlacePhoto(pin.id, result);
            setById((prev) => ({
              ...prev,
              [pin.id]: { status: "ready", result },
            }));
            const resolvedId = result.placeId?.trim();
            if (resolvedId && !pin.placeId?.trim()) {
              onPlaceIdResolvedRef.current?.(pin.id, resolvedId);
            }
          } else {
            setById((prev) => ({
              ...prev,
              [pin.id]: { status: "error" },
            }));
          }
        } catch {
          if (ac.signal.aborted) return;
          setById((prev) => ({
            ...prev,
            [pin.id]: { status: "error" },
          }));
        } finally {
          inFlight.current.delete(pin.id);
        }
      },
      ac.signal,
    );

    return () => {
      ac.abort();
    };
  }, [eligible, enabled]);

  const markBroken = useCallback((pinId: string) => {
    broken.current.add(pinId);
    invalidateCachedPlacePhoto(pinId);
    setById((prev) => ({
      ...prev,
      [pinId]: { status: "error" },
    }));
  }, []);

  const value = useMemo<PlacePhotosContextValue>(
    () => ({
      enabled,
      getPhoto: (pinId) => byId[pinId]?.result,
      getStatus: (pinId) => byId[pinId]?.status ?? "idle",
      markBroken,
    }),
    [byId, enabled, markBroken],
  );

  return createElement(PlacePhotosContext.Provider, { value }, children);
}

export function usePlacePhotos(): PlacePhotosContextValue {
  const ctx = useContext(PlacePhotosContext);
  if (!ctx) {
    return {
      enabled: false,
      getPhoto: () => undefined,
      getStatus: () => "idle",
      markBroken: () => undefined,
    };
  }
  return ctx;
}

/**
 * Resolve display URL/credit for a pin: Places photo preferred, Commons fallback.
 */
export function useResolvedPinPhoto(
  pin?: Pick<
    MapPin,
    | "id"
    | "imageUrl"
    | "imageAlt"
    | "imageCredit"
    | "name"
    | "lat"
    | "lng"
    | "mapsQuery"
    | "placeId"
  > | null,
): {
  url?: string;
  alt: string;
  credit?: string;
  source: "places" | "commons" | "none";
  onError: () => void;
} {
  const { getPhoto, markBroken } = usePlacePhotos();
  const places = pin ? getPhoto(pin.id) : undefined;

  const onError = useCallback(() => {
    if (pin && places) markBroken(pin.id);
  }, [markBroken, pin, places]);

  if (places?.mediaUrl) {
    return {
      url: places.mediaUrl,
      alt: pin?.imageAlt ?? pin?.name ?? "Place photo",
      credit: places.credit,
      source: "places",
      onError,
    };
  }

  if (pin?.imageUrl) {
    return {
      url: pin.imageUrl,
      alt: pin.imageAlt ?? pin.name ?? "Place photo",
      credit: pin.imageCredit,
      source: "commons",
      onError: () => undefined,
    };
  }

  return {
    url: undefined,
    alt: pin?.name ?? "Place photo",
    credit: undefined,
    source: "none",
    onError: () => undefined,
  };
}
